import { DeferredFooter } from "@/components/DeferredFooter";
import { SEO } from "@/components/SEO";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ArrowLeftRight,
  BarChart,
  Building2,
  Calculator,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  FileCheck,
  FileKey,
  FileSignature,
  FileText,
  HandCoins,
  Home,
  Layers,
  Percent,
  PiggyBank,
  Receipt,
  RefreshCw,
  Scale,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  TrendingDown,
  TrendingUp,
  Unlock,
  UserCheck,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import KlarsprakSearch from "@/components/klarsprak/KlarsprakSearch";

const iconMap: Record<string, LucideIcon> = {
  ArrowLeftRight,
  BarChart,
  Building2,
  Calculator,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  FileCheck,
  FileKey,
  FileSignature,
  FileText,
  HandCoins,
  Home,
  Layers,
  Percent,
  PiggyBank,
  Receipt,
  RefreshCw,
  Scale,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  TrendingDown,
  TrendingUp,
  Unlock,
  UserCheck,
  Users,
  Wallet,
};

const allGuides = [
  { section: "Kunskapsbank", description: "Grundläggande finansiella begrepp och beslut", guides: [
    { title: "Snittränta", description: "Genomsnittlig ränta över tid", slug: "snittranta", icon: "TrendingUp", gradient: "from-primary to-primary-dark" },
    { title: "Handpenning", description: "Eget kapital vid bostadsköp", slug: "handpenning", icon: "DollarSign", gradient: "from-primary to-primary-dark" },
    { title: "Amortera eller spara", description: "Strategisk ekonomisk planering", slug: "amortera-eller-spara", icon: "PiggyBank", gradient: "from-primary to-primary-dark" },
    { title: "Inflation", description: "Penningvärde och köpkraft", slug: "inflation", icon: "TrendingDown", gradient: "from-primary to-primary-dark" },
    { title: "ISK - Investeringssparkonto", description: "Schablonbeskattning för sparande", slug: "investeringssparkonto", icon: "Wallet", gradient: "from-primary to-primary-dark" },
    { title: "Gåvobrev", description: "Dokumentera gåvor mellan familjemedlemmar", slug: "gavobrev", icon: "FileText", gradient: "from-primary to-primary-dark" }
  ]},
  { section: "Viktiga lånebegrepp", description: "Juridiska termer och roller vid lån", guides: [
    { title: "Annuitetslån", description: "Lån med fast månadskostnad", slug: "annuitetslån", icon: "Calculator", gradient: "from-primary to-primary-dark" },
    { title: "Belåningsgrad", description: "Hur mycket du kan låna", slug: "belåningsgrad", icon: "Home", gradient: "from-primary to-primary-dark" },
    { title: "Kontantinsats och bolånetak", description: "85%-regeln förklarad", slug: "kontantinsats-och-bolanetak", icon: "Percent", gradient: "from-primary to-primary-dark" },
    { title: "Borgenär", description: "Den som lånar ut pengar", slug: "borgenär", icon: "Users", gradient: "from-primary to-primary-dark" },
    { title: "Borgensman", description: "Säkerhet för lån", slug: "borgensman", icon: "UserCheck", gradient: "from-primary to-primary-dark" },
    { title: "Gäldenär", description: "Den som lånar pengar", slug: "gäldenär", icon: "Scale", gradient: "from-primary to-primary-dark" }
  ]},
  { section: "Låneberäkningar & Räntor", description: "Förstå kostnader och räkna på olika lånealternativ", guides: [
    { title: "Effektiv ränta", description: "Total lånekostnad inklusive avgifter", slug: "effektiv-ranta", icon: "Percent", gradient: "from-primary to-primary-dark" },
    { title: "Ränterabatt", description: "Förhandla fram bättre bolåneränta", slug: "ranterabatt", icon: "TrendingDown", gradient: "from-primary to-primary-dark" },
    { title: "Ränteavdrag", description: "Få tillbaka 30% av räntan", slug: "ranteavdrag", icon: "Receipt", gradient: "from-primary to-primary-dark" },
    { title: "KALP - Kalkylerad årlig procentsats", description: "Jämför olika kreditkort", slug: "kalp", icon: "Calculator", gradient: "from-primary to-primary-dark" },
    { title: "Reporänta", description: "Riksbankens styrränta påverkar ditt bolån", slug: "reporanta", icon: "BarChart", gradient: "from-primary to-primary-dark" },
    { title: "Låneberäkningar", description: "Kalkyler för olika lån och sparande", slug: "lanekalkyl", icon: "Calculator", gradient: "from-primary to-primary-dark" }
  ]},
  { section: "Köpa Bostad", description: "Allt om bostadsköp och juridiska dokument", guides: [
    { title: "Köpekontrakt", description: "Juridiskt bindande avtal för bostad", slug: "kopekontrakt", icon: "FileKey", gradient: "from-slate-500 to-gray-500" },
    { title: "Köpebrev", description: "Ägarbevis för fastighet", slug: "kopebrev", icon: "FileSignature", gradient: "from-primary to-primary-dark" },
    { title: "Lagfart", description: "Registrera äganderätt hos Lantmäteriet", slug: "lagfart", icon: "FileCheck", gradient: "from-primary to-primary-dark" },
    { title: "Pantbrev", description: "Säkerhet för bolån", slug: "pantbrev", icon: "ShieldCheck", gradient: "from-primary to-primary-dark" },
    { title: "Lånelöfte", description: "Banktillsägelse innan budgivning", slug: "lanelöfte", icon: "CheckCircle", gradient: "from-primary to-primary-dark" },
    { title: "Uppskov", description: "Skjut upp reavinstskatt vid försäljning", slug: "uppskov", icon: "Clock", gradient: "from-primary to-primary-dark" }
  ]},
  { section: "Andra Lånetyper", description: "Speciallån och kompletterande finansiering", guides: [
    { title: "Företagslån för aktiebolag", description: "Finansiering för företag", slug: "foretagslan-aktiebolag", icon: "Building2", gradient: "from-primary to-primary-dark" },
    { title: "Lån utan säkerhet", description: "Blancolån utan pant", slug: "lan-utan-sakerhet", icon: "Unlock", gradient: "from-primary to-primary-dark" },
    { title: "Låna utan UC", description: "Privatlån utan kreditupplysning", slug: "lana-utan-uc", icon: "ShieldAlert", gradient: "from-primary to-primary-dark" },
    { title: "Refinansiering", description: "Slå samman dina lån till ett", slug: "refinansiering", icon: "RefreshCw", gradient: "from-primary to-primary-dark" },
    { title: "Samlingslån", description: "Samla alla skulder till ett lån", slug: "samlingslån", icon: "Layers", gradient: "from-primary to-primary-dark" },
    { title: "Överbrygningslån", description: "Tillfällig finansiering mellan köp och försäljning", slug: "overbryggningslan", icon: "ArrowLeftRight", gradient: "from-primary to-primary-dark" },
    { title: "Skuldsanering", description: "Juridisk skuldlättnad", slug: "skuldsanering", icon: "Scale", gradient: "from-primary to-primary-dark" },
    { title: "Låneskydd", description: "Försäkring för dina lån", slug: "laneskydd", icon: "Shield", gradient: "from-primary to-primary-dark" }
  ]},
  { section: "Betalkort & Krediter", description: "Kreditkort, bankkort och köp på kredit", guides: [
    { title: "Betalkort", description: "Skillnader mellan bankkort och kreditkort", slug: "betalkort", icon: "CreditCard", gradient: "from-primary to-primary-dark" },
    { title: "Bankkort och Kreditkort", description: "Debit vs kredit", slug: "bankkort-kreditkort", icon: "CreditCard", gradient: "from-primary to-primary-dark" },
    { title: "Kontantkort och prepaid", description: "Förbetald shoppingkort", slug: "kontantkort-prepaid", icon: "Wallet", gradient: "from-primary to-primary-dark" },
    { title: "Swish", description: "Mobilbetalningar mellan privatpersoner", slug: "swish", icon: "Smartphone", gradient: "from-primary to-primary-dark" },
    { title: "Klarna - Köp nu, betala senare", description: "Faktura och delbetalning", slug: "klarna-kop-nu-betala-senare", icon: "ShoppingCart", gradient: "from-primary to-primary-dark" }
  ]}
];

const KlarsprakPage = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "God morgon!";
    if (hour < 18) return "God dag!";
    return "God kväll!";
  };

  const handleGuideClick = (slug: string) => {
    const cleanSlug = slug.startsWith('/') ? slug.slice(1) : slug;
    navigate(`/${currentLanguage}/guider/${cleanSlug}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO title="Klarspråk - Allt om Lån och Ekonomi | Klarfinans" description="Lär dig allt om lån, bolån, räntor, kreditkort och ekonomisk planering. Enkla förklaringar på svenska av viktiga finansiella begrepp." keywords={["klarspråk", "ekonomiguide", "låneguide", "bolåneguide", "finansiella begrepp"]} />
      <main className="flex-1 pt-20">
        <section className="border-b bg-background py-10 md:py-14">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-semibold uppercase tracking-wide text-primary">35+ guider</p>
              <h1 className="mt-3 text-3xl font-bold leading-tight md:text-5xl">Guider i klarspråk</h1>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-muted-foreground">
                Korta förklaringar om lån, räntor, bostadsköp och vanliga ekonomiska begrepp.
              </p>
              <KlarsprakSearch allGuides={allGuides} onGuideClick={handleGuideClick} />
            </div>
          </div>
        </section>

        <section className="py-10 md:py-12">
          <div className="container mx-auto space-y-12 px-4">
            {allGuides.map((cat) => (
              <div key={cat.section} className="space-y-4">
                <div className="space-y-1">
                  <h2 className="text-xl font-bold md:text-2xl">{cat.section}</h2>
                  <p className="max-w-2xl text-sm leading-6 text-muted-foreground">{cat.description}</p>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {cat.guides.map(g => {
                    const Icon = iconMap[g.icon];
                    return (
                      <Card key={g.slug} className="group cursor-pointer transition-colors hover:border-primary/50 hover:bg-primary/5" onClick={() => handleGuideClick(g.slug)}>
                        <CardHeader className="flex flex-row items-start gap-3 space-y-0 p-4">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                            {Icon && <Icon className="h-5 w-5" />}
                          </div>
                          <div className="min-w-0 space-y-1">
                            <CardTitle className="text-base leading-6 transition-colors group-hover:text-primary">{g.title}</CardTitle>
                            <CardDescription className="text-sm leading-5">{g.description}</CardDescription>
                          </div>
                        </CardHeader>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t bg-muted/30 py-10 md:py-12">
          <div className="container mx-auto px-4">
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-bold md:text-3xl">Gå vidare till jämförelser</h2>
              <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                När du har läst klart kan du jämföra räntor, villkor och nästa steg.
              </p>
            </div>
            <div className="mx-auto grid max-w-6xl grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              {[
                { title: "Bolån", desc: "Räntor, banker och kalkyler", icon: Home, path: "/bolan" },
                { title: "Privatlån", desc: "Låneförmedlare och långivare", icon: HandCoins, path: "/privatlan" },
                { title: "Lån utan UC", desc: "Alternativ med annan kreditupplysning", icon: ShieldCheck, path: "/lana-utan-uc" },
                { title: "Företagslån", desc: "Finansiering för företag", icon: Building2, path: "/foretagslan" }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <Card key={i} className="group cursor-pointer transition-colors hover:border-primary/50 hover:bg-background" onClick={() => navigate(`/${currentLanguage}${item.path}`)}>
                    <CardHeader className="flex flex-row items-start gap-3 space-y-0 p-4">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="text-left">
                        <CardTitle className="text-base leading-6 transition-colors group-hover:text-primary">{item.title}</CardTitle>
                        <CardDescription className="mt-1 text-sm leading-5">{item.desc}</CardDescription>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <DeferredFooter />
    </div>
  );
};

export default KlarsprakPage;
